import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { CalendarClock, CheckCircle2, Clock, History, Plus, Video, X } from 'lucide-react';
import { Button, cn, Paginator } from '@jeraisonne/ui';
import { isVisioRoomJoinable, toEndedAtMs } from '@jeraisonne/db/visio';
import { RENDEZ_VOUS_KIND_LABELS } from '@jeraisonne/db';
import { useAuth } from '@/lib/auth';
import { useNow } from '@/hooks/use-now';
import { useJoinVisioRoom } from '@/lib/visio/handoff';
import {
  useArchiveRendezVous,
  useMyArchivedRendezVousIds,
  useUnarchiveRendezVous,
} from '@/lib/rendezvous-archive';
import { TutorBookingTabs } from '@/components/tutor-booking-tabs';
import {
  useTutorBookingSlots,
  useTutorPastBookings,
  bucketTutorBookingRows,
  type TutorBookingRow,
} from '@/lib/tutor/booking';
import { usePagedList } from '@/lib/use-paged-list';
import { dayLabel, parisDayKey } from '@/lib/tutor/dashboard-overview';

function formatSlotDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function formatPrice(cents: number): string {
  return (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

type Row = TutorBookingRow;

/** Réservations confirmées regroupées par jour (« Aujourd'hui », « Demain », date). */
function groupByDay(rows: Row[], nowMs: number): { key: string; label: string; rows: Row[] }[] {
  const days: { key: string; label: string; rows: Row[] }[] = [];
  for (const row of rows) {
    const key = parisDayKey(new Date(row.slot.starts_at).getTime());
    let day = days.find((d) => d.key === key);
    if (!day) {
      day = { key, label: dayLabel(row.slot.starts_at, nowMs), rows: [] };
      days.push(day);
    }
    day.rows.push(row);
  }
  return days;
}

const STATUS_META = {
  cancelled: { icon: History, color: 'text-ink-400', label: 'Annulée' },
  expired: { icon: History, color: 'text-ink-400', label: 'Expirée (non payée)' },
  refunded: { icon: History, color: 'text-ink-400', label: 'Remboursée' },
} as const;

function ReservationCard({
  row,
  onArchive,
  archiving,
  archivedByMe,
  onUnarchive,
  unarchiving,
  showDeadline = false,
}: {
  row: Row;
  /** Réservation en attente de paiement : affiche l'échéance du paiement. */
  showDeadline?: boolean;
  /** Présent uniquement sur les lignes "à venir" — masque le rendez-vous du dashboard, côté tuteur seulement. */
  onArchive?: () => void;
  archiving?: boolean;
  /** Présent sur les lignes déjà passées en Historique via archivage manuel (pas via le temps). */
  archivedByMe?: boolean;
  onUnarchive?: () => void;
  unarchiving?: boolean;
}) {
  const name =
    row.reservation.student_display_name ??
    row.reservation.guest_name ??
    row.reservation.guest_email;
  const now = useNow(30_000);
  const joinVisioRoom = useJoinVisioRoom();
  const joinable =
    row.reservation.status === 'confirmed' &&
    isVisioRoomJoinable(
      now,
      new Date(row.slot.starts_at).getTime(),
      new Date(row.slot.ends_at).getTime(),
      toEndedAtMs(row.reservation.ended_at),
    );

  return (
    <div
      className={cn(
        'border-ink-100 relative flex flex-col gap-3 rounded-2xl border bg-white p-4 sm:flex-row sm:items-center',
        onArchive && 'pr-11',
      )}
    >
      {onArchive && (
        <button
          type="button"
          onClick={onArchive}
          disabled={archiving}
          aria-label="Archiver ce rendez-vous"
          className="text-ink-300 hover:bg-ink-100 hover:text-ink-600 absolute right-0 top-0 flex h-11 w-11 items-center justify-center rounded-full transition-colors active:scale-95 disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-ink-900 truncate text-sm font-semibold">{name ?? 'Élève'}</p>
          <p className="text-ink-500 mt-0.5 text-xs capitalize">
            {formatSlotDate(row.slot.starts_at)}
          </p>
          <span className="bg-ink-100 text-ink-600 mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-semibold">
            {RENDEZ_VOUS_KIND_LABELS[row.slot.kind]}
          </span>
          {showDeadline && (
            <span className="mt-1 block font-sans text-xs font-semibold text-amber-700">
              Paiement attendu avant le {formatSlotDate(row.reservation.payment_deadline)}
            </span>
          )}
          {archivedByMe && (
            <span className="bg-accent-50 text-accent-700 ml-1.5 mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold">
              Archivé
            </span>
          )}
        </div>
        <p className="text-ink-700 shrink-0 font-sans text-sm font-semibold">
          {row.reservation.price_cents > 0 ? formatPrice(row.reservation.price_cents) : 'Gratuit'}
        </p>
      </div>
      {joinable && (
        <Button
          onClick={() => joinVisioRoom.mutate(row.reservation.id)}
          loading={joinVisioRoom.isPending}
          size="sm"
          className="min-h-[2.75rem] w-full active:scale-[0.98] sm:w-auto"
        >
          <Video className="size-4" />
          Rejoindre la salle
        </Button>
      )}
      {!joinable && row.reservation.status === 'confirmed' && (
        <Link
          to="/rendez-vous/$reservationId"
          params={{ reservationId: row.reservation.id }}
          className="border-ink-200 text-ink-700 hover:bg-ink-50 flex min-h-[2.75rem] shrink-0 items-center justify-center rounded-xl border px-3 text-xs font-semibold transition-colors active:scale-95 sm:w-auto"
        >
          Voir le détail
        </Link>
      )}
      {archivedByMe && onUnarchive && (
        <button
          type="button"
          onClick={onUnarchive}
          disabled={unarchiving}
          className="text-primary-600 min-h-[2.75rem] shrink-0 font-sans text-xs font-semibold underline active:scale-95 disabled:opacity-50"
        >
          Restaurer
        </button>
      )}
    </div>
  );
}

/** Bascule "À venir" / "Passés" — mobile-first, plein-largeur, touch target ≥44px. */
function ViewTabs({
  view,
  onChange,
}: {
  view: 'upcoming' | 'past';
  onChange: (view: 'upcoming' | 'past') => void;
}) {
  return (
    <div className="bg-ink-100 mt-4 flex gap-1 rounded-xl p-1">
      {(
        [
          { value: 'upcoming' as const, label: 'À venir' },
          { value: 'past' as const, label: 'Passés' },
        ] as const
      ).map((tab) => (
        <button
          key={tab.value}
          type="button"
          onClick={() => onChange(tab.value)}
          aria-pressed={view === tab.value}
          className={cn(
            'min-h-[2.75rem] flex-1 rounded-lg text-sm font-semibold transition-colors active:scale-[0.98]',
            view === tab.value ? 'text-ink-900 bg-white shadow-sm' : 'text-ink-500',
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function TutorBookingDashboardPage() {
  const { profile } = useAuth();
  const now = useNow(60_000);
  const [view, setView] = useState<'upcoming' | 'past'>('upcoming');
  const { data: slots = [], isLoading } = useTutorBookingSlots(profile?.id);
  const { data: pastRows = [], isLoading: pastLoading } = useTutorPastBookings(profile?.id);
  const { items: pagePast, paginator: pastPaginator } = usePagedList(pastRows, {
    unit: 'rendez-vous',
    label: 'Pages des rendez-vous passés',
  });
  const { data: archivedIds } = useMyArchivedRendezVousIds(profile?.id);
  const archive = useArchiveRendezVous(profile?.id);
  const unarchive = useUnarchiveRendezVous(profile?.id);

  function handleArchive(reservationId: string) {
    const confirmed = window.confirm(
      "Archiver ce rendez-vous ? Il n'apparaîtra plus dans tes rendez-vous à venir, mais tu pourras le retrouver dans Passés. L'élève continuera à le voir normalement.",
    );
    if (!confirmed) return;
    archive.mutate(reservationId);
  }

  const rows = useMemo<Row[]>(
    () => slots.flatMap((slot) => slot.reservations.map((reservation) => ({ reservation, slot }))),
    [slots],
  );

  const { upcomingConfirmed, pendingPayment } = useMemo(
    () => bucketTutorBookingRows(rows, now, archivedIds),
    [rows, now, archivedIds],
  );

  const isEmpty =
    view === 'upcoming'
      ? !isLoading && upcomingConfirmed.length === 0 && pendingPayment.length === 0
      : !pastLoading && pastRows.length === 0;

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-ink-900 text-2xl font-extrabold tracking-tight">
          Rendez-vous
        </h1>
        <Link
          to="/tutor/rendez-vous/new"
          className="bg-primary-500 font-display hover:bg-primary-600 inline-flex min-h-[2.75rem] items-center gap-2 rounded-xl px-4 text-sm font-bold text-white shadow-md transition-all active:scale-[.98]"
        >
          <Plus className="h-4 w-4" />
          Nouveau
        </Link>
      </div>
      <TutorBookingTabs />
      <ViewTabs view={view} onChange={setView} />

      {(view === 'upcoming' ? isLoading : pastLoading) && (
        <div className="flex justify-center py-10">
          <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      )}

      {isEmpty && (
        <div className="border-ink-100 mt-4 rounded-2xl border border-dashed bg-white p-6 text-center">
          <CalendarClock className="text-ink-300 mx-auto h-8 w-8" />
          <p className="text-ink-700 mt-2 font-sans text-sm font-semibold">
            {view === 'upcoming' ? 'Aucune réservation à venir' : 'Aucun rendez-vous passé'}
          </p>
          <p className="text-ink-500 mt-1 font-sans text-sm">
            {view === 'upcoming'
              ? 'Ouvre des créneaux chaque semaine pour être réservé, ou programme toi-même un rendez-vous avec un élève.'
              : 'Tes rendez-vous terminés, annulés ou archivés apparaîtront ici.'}
          </p>
          {view === 'upcoming' && (
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <Link
                to="/tutor/rendez-vous/creneaux"
                className="bg-primary-500 hover:bg-primary-600 inline-flex min-h-[2.75rem] items-center justify-center gap-2 rounded-xl px-4 font-sans text-sm font-semibold text-white active:scale-[0.98]"
              >
                <CalendarClock className="h-4 w-4" aria-hidden />
                Ouvrir mes créneaux
              </Link>
              <Link
                to="/tutor/rendez-vous/new"
                className="border-ink-200 text-ink-800 inline-flex min-h-[2.75rem] items-center justify-center gap-2 rounded-xl border px-4 font-sans text-sm font-semibold active:scale-[0.98]"
              >
                <Video className="h-4 w-4" aria-hidden />
                Programmer un rendez-vous
              </Link>
            </div>
          )}
        </div>
      )}

      {view === 'upcoming' && (
        <div className="mt-4 space-y-6">
          {groupByDay(upcomingConfirmed, now).map((day) => (
            <section key={day.key} className="space-y-2" aria-label={day.label}>
              <p className="font-display text-ink-900 flex items-center gap-1.5 text-sm font-extrabold first-letter:uppercase">
                <CheckCircle2 className="text-xp-500 h-4 w-4" aria-hidden />
                {day.label}
                <span className="text-ink-400 font-sans text-xs font-semibold">
                  ({day.rows.length})
                </span>
              </p>
              {day.rows.map((row) => (
                <ReservationCard
                  key={row.reservation.id}
                  row={row}
                  onArchive={() => handleArchive(row.reservation.id)}
                  archiving={archive.isPending && archive.variables === row.reservation.id}
                />
              ))}
            </section>
          ))}

          {pendingPayment.length > 0 && (
            <section className="space-y-2">
              <p className="font-display text-ink-900 flex items-center gap-1.5 text-sm font-extrabold">
                <Clock className="text-accent-500 h-4 w-4" aria-hidden />
                En attente de paiement
                <span className="text-ink-400 font-sans text-xs font-semibold">
                  ({pendingPayment.length})
                </span>
              </p>
              <p className="text-ink-500 font-sans text-xs">
                Sans paiement avant l’échéance, la place se libère automatiquement.
              </p>
              {pendingPayment.map((row) => (
                <ReservationCard key={row.reservation.id} row={row} showDeadline />
              ))}
            </section>
          )}
        </div>
      )}

      {view === 'past' && pastRows.length > 0 && (
        <div className="mt-4 space-y-6">
          <section className="space-y-2">
            <p className="text-ink-400 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider">
              <History className="h-3.5 w-3.5" />
              Passés ({pastRows.length})
            </p>
            {pagePast.map((row) => {
              const meta =
                row.reservation.status in STATUS_META
                  ? STATUS_META[row.reservation.status as keyof typeof STATUS_META]
                  : null;
              const archivedByMe = !!archivedIds?.has(row.reservation.id);
              return (
                <div key={row.reservation.id} className="">
                  <ReservationCard
                    row={row}
                    archivedByMe={archivedByMe}
                    onUnarchive={
                      archivedByMe ? () => unarchive.mutate(row.reservation.id) : undefined
                    }
                    unarchiving={unarchive.isPending && unarchive.variables === row.reservation.id}
                  />
                  {meta && (
                    <div className="px-4 pb-2 text-right">
                      <span
                        className={cn('shrink-0 font-sans text-[11px] font-semibold', meta.color)}
                      >
                        {meta.label}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
            <Paginator {...pastPaginator} />
          </section>
        </div>
      )}
    </div>
  );
}
